import {isEmpty} from 'lodash-es';
import {useCallback, useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import useFilters from '@/hooks/useFilters';
import {userService} from '@/services/userService';
import type {AdminAfriexPerson, AfriexQueryParams} from '@/types';

import type {UserListProps} from './types';

const useUserList = (): UserListProps => {
  const [userList, setUserList] = useState<AdminAfriexPerson[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [userTotal, setUserTotal] = useState<number>(0);
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [isPageReset, setIsPageReset] = useState<boolean>(false);
  const [error, setError] = useState<unknown>(null);

  const [currentFilters, setCurrentFilters] = useState<AfriexQueryParams>({});
  const [filtersToApply, setFiltersToApply] = useState<AfriexQueryParams>({});
  const [filterCount, setFilterCount] = useState(0);
  const {applyFilters} = useFilters(setCurrentFilters);

  const fetchAllUsers = useCallback(
    async (params?: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const {data: users, total} = await userService.getUsers({
          ...params,
          ...currentFilters,
        });
        setUserList(users);
        setUserTotal(total || 0);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [currentFilters],
  );

  const handleAutoComplete = useCallback(
    async (searchTerm: string) => {
      try {
        setError(null);
        setIsLoading(true);
        setShowPagination(false);

        if (!searchTerm?.trim() || searchTerm.length < 3) {
          await fetchAllUsers({});
          return;
        }

        const users = await userService.searchAutoComplete(searchTerm);
        if (users) {
          setUserList(users);
          setUserTotal(1);
          setIsSearch(true);
        }
      } catch (e) {
        setError(e);
      }
      setIsLoading(false);
    },
    [fetchAllUsers],
  );

  useEffect(() => {
    // Fire-and-forget: fetchAllUsers try/catches, stores the failure in
    // `error` and clears `isLoading`, so this only stops an unexpected throw
    // becoming an unhandled rejection.
    fetchAllUsers({}).catch(() => {});
  }, [fetchAllUsers]);

  const fetchUserByAccountNumber = useCallback(
    async (bankAccountNumber: string) => {
      try {
        setError(null);
        setIsLoading(true);
        const user =
          await userService.getUserByBankAccountNumber(bankAccountNumber);

        const userList = !isEmpty(user) ? [user] : [];
        const userTotal = !isEmpty(user) ? 1 : 0;
        setUserList(userList);
        setUserTotal(userTotal);
        if (userTotal === 0) {
          toast.error('Bank account not associated with a user');
        }
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  // Modified handleApplyFilters to decide which API to call
  const handleApplyFilters = useCallback(() => {
    const {bankAccountNumber} = filtersToApply;
    if (
      bankAccountNumber &&
      typeof bankAccountNumber === 'string' &&
      bankAccountNumber.trim() !== ''
    ) {
      // Fire-and-forget: handleApplyFilters is synchronous (it is declared
      // `=> void` on UserListProps and is passed straight to a click handler),
      // and fetchUserByAccountNumber try/catches, stores the failure in
      // `error` and clears `isLoading`. The catch only stops an unexpected
      // throw becoming an unhandled rejection.
      fetchUserByAccountNumber(bankAccountNumber).catch(() => {});
      return;
    }

    applyFilters(filtersToApply);
    setIsPageReset((prev) => !prev); // Reset pagination to the first page
  }, [applyFilters, filtersToApply, fetchUserByAccountNumber]);

  // Handle change for instant data fetching
  const selectCurrentFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setCurrentFilters((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        return updatedFilters;
      });
      setIsPageReset((prev) => !prev);
    },
    [],
  );

  const selectAppliedFilters = useCallback(
    (name: string, value: string | Date | undefined) => {
      setFiltersToApply((prev) => {
        const updatedFilters = {
          ...prev,
          [name]: value,
        };
        // Only count non-undefined values
        const filteredCount = Object.entries(updatedFilters).filter(
          ([_, val]) => val !== undefined,
        ).length;
        setFilterCount(filteredCount);
        return updatedFilters;
      });
    },
    [],
  );

  const cleanFilters = () => {
    setCurrentFilters({});
    setFiltersToApply({});
    setFilterCount(0);
    setIsPageReset((prev) => !prev);
  };

  // `UserListProps.handleAutoComplete` is declared `=> void` and the value is
  // handed to SearchBar, whose own prop is declared the same way, so wrap
  // rather than widen. handleAutoComplete already try/catches into `error`;
  // the catch only stops an unexpected throw becoming an unhandled rejection.
  // useCallback keeps the identity stable across renders.
  const onAutoComplete = useCallback(
    (searchTerm: string) => {
      handleAutoComplete(searchTerm).catch(() => {});
    },
    [handleAutoComplete],
  );

  return {
    error,
    fetchUserList: fetchAllUsers,
    isLoading,
    isSearch,
    setPage,
    pageInfo: {
      page,
      total: userTotal,
    },
    showPagination,
    userList,
    filterCount,
    isPageReset,
    currentFilters, // automatically triggers fetch
    filtersToApply, // requires handleApplyFilters to trigger fetch
    clearFilters: cleanFilters,
    selectAppliedFilters, // replaces handleDateChange
    handleApplyFilters,
    selectCurrentFilters, // replaces handleFilterChange
    handleAutoComplete: onAutoComplete,
  };
};

export default useUserList;
